import baseAxios from 'redaxios'
import { expireSession, isSessionExpired, SessionExpiredError } from './session-expiry'

interface HeadersObject extends Record<string, string> {
  'Content-Type': string
  'pragma': string
  'cache-control': string
  'Accept-Language': string
}

function getHeaders(): HeadersObject {
  const headers: HeadersObject = {
    'Content-Type': 'application/json',
    'pragma': 'no-cache',
    'cache-control': 'no-cache',
    'Accept-Language': window ? window.navigator.language : 'en',
  }

  const csrfToken = document.querySelector<HTMLInputElement>('#csrf > input')

  if (csrfToken !== null)
    headers['X-RequestVerificationToken'] = csrfToken.value

  return headers
}

function parseUrl(value: string) {
  try {
    return new URL(value, window.location.origin)
  }
  catch {
    return null
  }
}

function getUrl(input: RequestInfo | URL) {
  if (typeof window === 'undefined') return null

  if (typeof input === 'string' || input instanceof URL)
    return parseUrl(input.toString())

  return parseUrl(input.url)
}

function isSameOriginApiRequest(input: RequestInfo | URL) {
  const url = getUrl(input)
  if (!url) return false

  return url.origin === window.location.origin && url.pathname.toLowerCase().startsWith('/api/')
}

function isAuthPath(pathname: string) {
  const path = pathname.toLowerCase()

  return (
    path.startsWith('/account/login')
    || path.startsWith('/saml/initiatesinglesignon')
    || path.startsWith('/saml/login')
  )
}

function isHtmlResponse(response: Response) {
  return response.headers.get('content-type')?.toLowerCase().includes('text/html') ?? false
}

function isRedirectResponse(response: Response) {
  return response.type === 'opaqueredirect' || (response.status >= 300 && response.status < 400)
}

function getRedirectUrl(response: Response) {
  const location = response.headers.get('location')

  if (location) return parseUrl(location)
  if (response.url) return parseUrl(response.url)

  return null
}

function isAuthRedirectUrl(url: URL) {
  return url.origin !== window.location.origin || isAuthPath(url.pathname)
}

async function readResponseText(response: Response) {
  try {
    return await response.clone().text()
  }
  catch {
    return ''
  }
}

function htmlLooksLikeLoginPage(body: string) {
  const normalized = body.toLowerCase()

  return (
    normalized.includes('/account/login')
    || normalized.includes('/saml/initiatesinglesignon')
    || normalized.includes('signin-oidc')
    || normalized.includes('login.windows.net')
  )
}

async function isExpiredSessionResponse(response: Response) {
  if (response.type === 'opaqueredirect') return true

  if (isRedirectResponse(response)) {
    const redirectUrl = getRedirectUrl(response)

    if (redirectUrl && isAuthRedirectUrl(redirectUrl)) return true
  }

  const body = response.status === 401 || response.status === 403 || isHtmlResponse(response)
    ? await readResponseText(response)
    : ''

  if ((response.status === 401 || response.status === 403) && body.trim() === '') return true

  const responseUrl = response.url ? parseUrl(response.url) : null
  if (response.redirected && responseUrl) {
    if (isAuthRedirectUrl(responseUrl)) return true
    if (!responseUrl.pathname.toLowerCase().startsWith('/api/') && isHtmlResponse(response)) return true
  }

  return isHtmlResponse(response) && htmlLooksLikeLoginPage(body)
}

async function fetchWithSessionExpiry(input: RequestInfo | URL, init?: RequestInit) {
  const isApiRequest = isSameOriginApiRequest(input)

  if (isApiRequest && isSessionExpired.value)
    throw new SessionExpiredError()

  const response = await fetch(input, isApiRequest ? { ...init, redirect: 'manual' } : init)

  if (isApiRequest && await isExpiredSessionResponse(response)) {
    expireSession()
    throw new SessionExpiredError()
  }

  return response
}

export const axios = baseAxios.create({
  headers: getHeaders(),
  withCredentials: true,
  fetch: fetchWithSessionExpiry,
})

export function sanitizeObject<T extends Record<string, any>>(obj: T): Partial<T> {
  return Object.fromEntries(
    Object.entries(obj).filter(([_, value]) => value !== ''),
  ) as Partial<T>
}
